import { Link, LinkProps, useSearchParams } from "react-router";
import { forwardRef } from "react";

const PRESERVED_PARAMS = ["companyId", "brandId"];

/**
 * Custom Link component that preserves the companyId and brandId query params across navigation.
 * Use this instead of react-router's Link for internal navigation.
 */
export const CmLink = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, children, ...props }, ref) => {
    const [searchParams] = useSearchParams();

    const carried = PRESERVED_PARAMS
      .map((key) => [key, searchParams.get(key)] as const)
      .filter((entry): entry is [string, string] => entry[1] !== null);

    // Build the destination URL with those params preserved
    let destination = to;

    if (carried.length > 0 && typeof to === "string") {
      const url = new URL(to, window.location.origin);
      for (const [key, value] of carried) url.searchParams.set(key, value);
      destination = url.pathname + url.search;
    } else if (carried.length > 0 && typeof to === "object") {
      const params = new URLSearchParams(to.search || "");
      for (const [key, value] of carried) params.set(key, value);
      destination = {
        ...to,
        search: params.toString(),
      };
    }

    return (
      <Link ref={ref} to={destination} {...props}>
        {children}
      </Link>
    );
  }
);

CmLink.displayName = "CmLink";
